/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useState} from "react"
import FullCalendar from "@fullcalendar/react"
import timeGridPlugin from "@fullcalendar/timegrid"
import interactionPlugin from "@fullcalendar/interaction"
import dayGridPlugin from "@fullcalendar/daygrid"
import {useTranslation} from "react-i18next"
import moment from "moment"
import "moment/min/locales"
import {BaseLoading, Icon} from "components"
import {CalendarProps} from "types/calendar"
import {CalendarPicker, ViewOptions, EventContent, MoreLinkContent, ConnectedCalendars, EventCard} from "./parts"
import styles from "./Calendar.module.css"
import {get, head, isEmpty, map, uniq} from "lodash"
import {useModel} from "hooks"
import {Auth} from "types/auth"
import {useQuery} from "@tanstack/react-query"
import {calendarService, eventTypeService} from "services"
import cx from "classnames"
import CalendarEventItem from "sections/shared/student/CalendarEventsTable/CalendarEventItem"
import {handleError} from "helpers"
import {Settings} from "types/settings"

const VIEWS = {
    DAY: "timeGridDay",
    MONTH: "dayGridMonth",
    WEEK: "timeGridWeek",
    YEAR: "YEAR"
}

export const Calendar = (props: CalendarProps) => {
    const {t} = useTranslation(["calendar", "common"])
    const [selectedDay, setSelectedDay] = useState(new Date())
    const [allEvents, setAllEvents] = useState([])
    const [clickInfo, setClickInfo] = useState<any>({})
    const [isShowMoreEvent, setIsShowMoreEvent] = useState(false)
    const [isShowEventPreview, setIsShowEventPreview] = useState(null)
    const [moreLinkData, setMoreLinkData] = useState(null)
    const [updateViewTo, setUpdateViewTo] = useState(VIEWS.WEEK)
    const [viewMode, setViewMode] = useState<"list" | "calendar">("calendar")
    const model = useModel()
    const {
        calendarRef,
        events,
        isLoading,
        onDateClick,
        onChangeDatesSet,
        eventOptions,
        viewOptions,
        isShowGoing,
        workDays,
        holidayDays,
        displayMode = "normal",
        renderWorkWeek,
        isWorkScheduleView = false,
        showConnectedCalendars,
        linkedCalendars,
        staticOptions,
        departmentId,
        updateLinkedCalendars,
        updateStaticOptions,
        ...rest
    } = props
    const isFullDisplay = displayMode === "normal"
    const eventTypeIds = uniq(map(allEvents, (ev) => ev.json_data?.eventTypeId)).filter(Boolean)

    const {data: outcomeEventTypes} = useQuery({
        queryKey: ["outcomeEventTypes", eventTypeIds],
        queryFn: () => eventTypeService.getEventTypes({filter: {id: eventTypeIds}, linkedEntities: ["outcomeGroups"]}),
        enabled: eventTypeIds.length > 0
    })

    useEffect(() => {
        if (calendarRef.current) {
            calendarRef.current.getApi().gotoDate(selectedDay)
        }
    }, [selectedDay])

    useEffect(() => {
        setAllEvents(!departmentId ? events : events.filter((event) => event.json_data.departmentId === departmentId))
    }, [events, departmentId])

    const onChangeView = (value) => {
        setUpdateViewTo(VIEWS[value])
        if (value !== "YEAR") {
            calendarRef.current.getApi().changeView(VIEWS[value])
        }
    }

    const changeToDayView = (date) => {
        setUpdateViewTo("DAY")
        calendarRef.current.getApi().gotoDate(date)
        setSelectedDay(date)
    }

    const onClickToday = () => {
        const today = new Date()
        calendarRef.current.getApi().gotoDate(today)
        setSelectedDay(today)
    }

    const onClickPrevMonth = () => {
        switch (updateViewTo) {
            case VIEWS.DAY: {
                const prevDay = moment(selectedDay).subtract(1, "day").toDate()
                setSelectedDay(prevDay)
                break
            }
            case VIEWS.WEEK: {
                const prevWeek = moment(selectedDay).subtract(1, "week").toDate()
                setSelectedDay(prevWeek)
                break
            }
            case VIEWS.MONTH: {
                const prevMonth = moment(selectedDay).subtract(1, "month").toDate()
                setSelectedDay(prevMonth)
                break
            }
            default:
                break
        }
    }

    const onClickNextMonth = () => {
        switch (updateViewTo) {
            case VIEWS.DAY: {
                const prevDay = moment(selectedDay).add(1, "day").toDate()
                setSelectedDay(prevDay)
                break
            }
            case VIEWS.WEEK: {
                const prevWeek = moment(selectedDay).add(1, "week").toDate()
                setSelectedDay(prevWeek)
                break
            }
            case VIEWS.MONTH: {
                const prevMonth = moment(selectedDay).add(1, "month").toDate()
                setSelectedDay(prevMonth)
                break
            }
            default:
                break
        }
    }

    const onDayClick = (date) => {
        setSelectedDay(date)
    }

    const handleDateClick = (date) => {
        if (isShowEventPreview || isShowMoreEvent) {
            return
        }
        onDateClick(date)
    }

    const handleEventClick = (clickInfo) => {
        setClickInfo(clickInfo)
    }

    const handleShowEventPreview = (visible) => {
        setIsShowEventPreview(visible)
    }

    const handleShowMoreEventPreview = (visible) => {
        setIsShowMoreEvent(visible)
    }

    const renderEventContent = (eventInfo) => {
        return (
            <EventContent
                updateViewTo={updateViewTo}
                eventInfo={eventInfo}
                clickInfo={clickInfo}
                eventOptions={eventOptions}
                isShowGoing={isShowGoing}
                linkedCalendars={linkedCalendars}
                handleShowEventPreview={handleShowEventPreview}
                isWorkScheduleView={isWorkScheduleView}
            />
        )
    }

    const handleMoreLinkClick = (info) => {
        setIsShowMoreEvent(true)
        setMoreLinkData(info)
        setClickInfo(info)
    }

    const renderMoreLinkContent = ({text}) => {
        return (
            <MoreLinkContent
                text={text}
                moreLinkData={moreLinkData}
                updateViewTo={updateViewTo}
                clickInfo={clickInfo}
                eventOptions={eventOptions}
                handleShowEventPreview={handleShowEventPreview}
                handleShowMoreEventPreview={handleShowMoreEventPreview}
                onClickDateDetail={onClickDateDetail}
                isWorkScheduleView={isWorkScheduleView}
                isShowGoing={isShowGoing}
            />
        )
    }

    const onClickDateDetail = (date) => {
        setUpdateViewTo("DAY")
        setSelectedDay(date)
    }

    const handleUpdateOutcome = async (event, outcomeId) => {
        const objectId = get(event, "id")
        if (!objectId || !outcomeId) return
        try {
            await calendarService.updateObjectOutcome({object_id: +objectId, outcome_id: outcomeId})
        } catch (error) {
            handleError(error)
        }
    }

    const getOutcomes = (event) => {
        const {eventTypeId} = event.json_data || {}
        const outcomeGroup: Settings.OutcomeGroup = head(
            outcomeEventTypes?.data?.find((outcome) => outcome.eventTypeId === eventTypeId)?.outcomeGroups
        )
        return outcomeGroup?.outcomes || []
    }

    const findOutcome = (event) => {
        const {completedOutcomeId} = event.json_data || {}
        if (!completedOutcomeId) return null
        const outcomes = getOutcomes(event)
        return outcomes.find((outcome) => outcome.outcomeId === completedOutcomeId)
    }

    const renderFullCalender = () => {
        return (
            <FullCalendar
                events={allEvents}
                initialView={VIEWS.WEEK}
                eventOverlap={false}
                editable={false}
                selectable={false}
                selectMirror
                allDaySlot
                dayMaxEvents={2}
                dayMaxEventRows={2}
                slotDuration="01:00:00"
                scrollTime="08:00:00"
                firstDay={0}
                contentHeight={renderWorkWeek ? 880 : 600}
                eventOrder="order"
                nowIndicator
                navLinks
                navLinkDayClick={changeToDayView}
                // TODO locale={model.getFullCalendarLocale()}
                datesSet={onChangeDatesSet}
                ref={calendarRef}
                plugins={[timeGridPlugin, interactionPlugin, dayGridPlugin]}
                customButtons={{
                    todayBtn: {
                        text: "Today",
                        click: onClickToday
                    },
                    prevIcon: {
                        icon: "chevron-left",
                        click: onClickPrevMonth
                    },
                    nextIcon: {
                        icon: "chevron-right",
                        click: onClickNextMonth
                    }
                }}
                headerToolbar={{
                    start: "todayBtn prevIcon,nextIcon",
                    center: "title",
                    end: ""
                }}
                dayCellClassNames={({date}) => {
                    const dayOfWeek = moment(date).day()
                    if (!isEmpty(holidayDays)) {
                        const isHolidayDay = holidayDays.some((holidayDay) =>
                            moment(date).startOf("date").isSame(moment(holidayDay).startOf("date"))
                        )
                        if (isHolidayDay) {
                            return "holiday-day"
                        }
                    }
                    return ""
                }}
                dayHeaderFormat={({date, localeCodes}) => {
                    const newDate = moment(date.array).locale(localeCodes[0])
                    if (updateViewTo === VIEWS.MONTH) {
                        return `${moment(newDate).format("ddd")}`
                    }
                    return `${moment(newDate).format("ddd")} \n ${moment(newDate).format("D")}`
                }}
                views={{
                    timeGridWeek: {
                        titleFormat: {year: "numeric", month: "long"}
                    }
                }}
                eventTimeFormat={{
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false
                }}
                slotLabelFormat={{
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: model.user?.timeFormat === Auth.TimeFormat.Time_12
                }}
                scrollTimeReset={false}
                dateClick={handleDateClick}
                eventContent={renderEventContent}
                eventClick={handleEventClick}
                moreLinkContent={renderMoreLinkContent}
                moreLinkClick={handleMoreLinkClick}
                {...rest}
            />
        )
    }

    const renderCalendarEventsTable = () => {
        if (isEmpty(allEvents)) {
            return <div>No Event Found</div>
        }
        return (
            <div className={styles.calendarEventsTable}>
                {allEvents.map((event) => (
                    <CalendarEventItem
                        key={event.id}
                        event={event}
                        isCalendarEvent
                        outcomes={getOutcomes(event)}
                        outcomeValue={findOutcome(event)}
                        handleUpdateOutcome={handleUpdateOutcome}
                    />
                ))}
            </div>
        )
    }

    // Display in Panel
    if (!isFullDisplay) {
        return (
            <div className={styles.panelWrap}>
                <CalendarPicker selectedDay={selectedDay} onDayClick={onDayClick} displayMode={displayMode} />
                <div className={styles.hideCalendar}>{renderFullCalender()}</div>
                <BaseLoading isShow={isLoading} />
                {allEvents.length > 0 ? (
                    allEvents.map((item) => (
                        <EventCard
                            key={item.id}
                            eventInfo={item}
                            eventOptions={eventOptions}
                            outcomeEventTypes={outcomeEventTypes}
                        />
                    ))
                ) : (
                    <div>No Event Found</div>
                )}
            </div>
        )
    }

    return (
        <div className={styles.root}>
            <div className={styles.leftSide}>
                <div className={styles.viewOptionsWrap}>
                    <ViewOptions viewOptions={viewOptions} t={t} onChange={onChangeView} updateViewTo={updateViewTo} />
                </div>
                <CalendarPicker selectedDay={selectedDay} onDayClick={onDayClick} />
                {showConnectedCalendars && (
                    <ConnectedCalendars
                        linkedCalendars={linkedCalendars}
                        staticOptions={staticOptions}
                        updateLinkedCalendars={updateLinkedCalendars}
                        updateStaticOptions={updateStaticOptions}
                    />
                )}
                {renderWorkWeek?.()}
            </div>
            <div className={styles.rightSide}>
                <BaseLoading isShow={isLoading} />
                <div className={styles.viewModeSwitcher}>
                    <div
                        className={cx(styles.viewModeSwitcherItem, {
                            [styles.viewModeSwitcherItemActive]: viewMode === "calendar"
                        })}
                        onClick={() => setViewMode("calendar")}>
                        <Icon icon="CALENDAR" className={styles.viewModeSwitcherItemIcon} />
                    </div>
                    <div
                        className={cx(styles.viewModeSwitcherItem, {
                            [styles.viewModeSwitcherItemActive]: viewMode === "list"
                        })}
                        onClick={() => setViewMode("list")}>
                        <Icon icon="VIEW_LIST" className={styles.viewModeSwitcherItemIcon} />
                    </div>
                </div>
                <div className={cx({[styles.calendarHidden]: viewMode === "list"})}>{renderFullCalender()}</div>
                {viewMode === "list" && renderCalendarEventsTable()}
            </div>
        </div>
    )
}
